import React, { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Trophy, X } from "lucide-react";
import { api } from "@/lib/api";

const CELEBRATED_KEY = "bpt_milestone_celebrated";

function getCelebrated() {
  try { return JSON.parse(localStorage.getItem(CELEBRATED_KEY) || "[]"); }
  catch { return []; }
}
function markCelebrated(key) {
  const arr = getCelebrated();
  if (!arr.includes(key)) {
    arr.push(key);
    localStorage.setItem(CELEBRATED_KEY, JSON.stringify(arr.slice(-50)));
  }
}

function launchConfetti() {
  // Skip confetti op kleine schermen (blokkeert mobile UX)
  if (typeof window !== "undefined" && window.innerWidth < 768) return;
  const end = Date.now() + 1500;
  const colors = ["#F26522", "#0D3B66", "#22c55e", "#eab308", "#a855f7"];
  (function frame() {
    confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

/**
 * MilestoneCelebration — polling widget die op recent-bereikte succes-mijlpalen let.
 * Bij nieuwe milestone (nog niet lokaal 'gevierd') → confetti + banner.
 */
export default function MilestoneCelebration() {
  const [milestone, setMilestone] = useState(null);

  useEffect(() => {
    let mounted = true;
    const check = async () => {
      try {
        const { data } = await api.get("/jubilea/vandaag");
        if (!mounted) return;
        const list = data.recente_milestones || [];
        const celebrated = getCelebrated();
        const nieuw = list.find(m => {
          const key = `${m.kpi}:${m.drempel}`;
          return !celebrated.includes(key);
        });
        if (nieuw) {
          setMilestone(nieuw);
          launchConfetti();
        }
      } catch { /* silent */ }
    };
    check();
    // Poll elke 5 minuten
    const t = setInterval(check, 5 * 60 * 1000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // Auto-hide na 6 seconden zodat banner niet blijft plakken tijdens navigatie
  useEffect(() => {
    if (!milestone) return;
    const key = `${milestone.kpi}:${milestone.drempel}`;
    const t = setTimeout(() => { markCelebrated(key); setMilestone(null); }, 6000);
    return () => clearTimeout(t);
  }, [milestone]);

  if (!milestone) return null;

  // Skip volledig op mobiel — banner blokkeerde FAB/mic en gaf UX-uitval
  if (typeof window !== "undefined" && window.innerWidth < 768) return null;

  const key = `${milestone.kpi}:${milestone.drempel}`;
  const close = () => { markCelebrated(key); setMilestone(null); };  const labelKpi = ({
    werkbonnen_afgehandeld: "afgehandelde werkbonnen",
    klanten_totaal: "klanten in database",
    onderhoudscertificaten: "onderhoudscertificaten",
    verify_hits: "verify-scans van QR-certificaten",
    omzet_ytd_eur: "euro omzet dit jaar",
  })[milestone.kpi] || milestone.kpi;

  const bedrag = milestone.kpi === "omzet_ytd_eur"
    ? `€ ${(milestone.drempel).toLocaleString("nl-NL")}`
    : (milestone.drempel).toLocaleString("nl-NL");

  return (
    <Card
      className="fixed bottom-3 right-3 left-3 sm:left-auto sm:bottom-4 sm:right-4 z-50 sm:max-w-md shadow-2xl border-2 border-[#F26522] animate-in slide-in-from-bottom sm:slide-in-from-right"
      data-testid="milestone-celebration"
    >
      <CardContent className="p-3 sm:p-4 relative">
        <button
          onClick={close}
          className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 text-slate-400 hover:text-slate-700 p-1"
          data-testid="milestone-close"
          aria-label="Sluiten"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="flex items-start gap-2 sm:gap-3 pr-6">
          <div className="bg-gradient-to-br from-orange-400 to-amber-500 p-2 sm:p-3 rounded-full shrink-0">
            <Trophy className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] sm:text-xs font-bold text-[#F26522] uppercase tracking-wider">
              🎉 Mijlpaal bereikt!
            </div>
            <h3 className="text-sm sm:text-lg font-bold text-[#0D3B66] mt-0.5 sm:mt-1 truncate">
              {bedrag} {labelKpi}
            </h3>
            <p className="text-[11px] sm:text-xs text-slate-600 mt-0.5 sm:mt-1">
              Chapeau team — samen bereikt.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
